/**
 * Pure logic behind PromptDraftEditor's Save button (PROC-190).
 *
 * response_schema/sample_output are DB-only columns everywhere else in
 * this codebase (Rio's apply_update_prompt_row tool, the Prompt
 * Constructor's draft_fields_to_apply deliberately excludes them) and
 * must never be staged in draft_overrides — a stray staged value used
 * to permanently mask a live update made through any other path (Rio's
 * direct writes, most visibly). Extracted from the component so the
 * split between "goes to draft_overrides" and "writes the live row
 * directly" is covered without rendering the form.
 */

export type DraftOverrides = Partial<{
  instructions: string;
  role: string | null;
  templated_instructions: string | null;
  technical_instructions: string | null;
  inputs: { key: string }[];
}>;

export interface LiveUpdatePayload {
  response_schema?: unknown;
  sample_output?: string | null;
}

export interface DirtyFlags {
  role?: boolean;
  templated_instructions?: boolean;
  instructions?: boolean;
  technical_instructions?: boolean;
  sample_output?: boolean;
  response_schema?: boolean;
  // react-hook-form tracks array fields as a per-item boolean array,
  // not a single flag — any non-empty array is truthy, same as a
  // plain `true` for our purposes here.
  input_keys?: boolean | boolean[];
}

export interface FormValues {
  role: string;
  templated_instructions: string;
  instructions: string;
  technical_instructions: string;
  sample_output: string;
  response_schema: string;
  input_keys: string[];
}

export interface SplitDirtyFieldsResult {
  nextOverrides: DraftOverrides;
  liveUpdate: LiveUpdatePayload;
  /** Whether any draft_overrides-eligible field is dirty — callers
   *  should skip the draft PUT entirely when this is false (e.g. only
   *  response_schema changed), rather than send a no-op write. */
  draftDirty: boolean;
  /** Set when response_schema is dirty but isn't valid JSON. Callers
   *  should surface this and abort before firing either mutation. */
  error?: string;
}

const LIVE_ONLY_KEYS = ["response_schema", "sample_output"] as const;

/**
 * Drop response_schema/sample_output from a raw draft_overrides
 * object. TS types alone don't stop a stray key from surviving a plain
 * object spread at runtime — a prompt saved before this fix can still
 * carry one of these keys in its actual stored JSON regardless of what
 * DraftOverrides declares. Scrubbing here means the next save of any
 * other field on that prompt cleans it up rather than perpetuating it.
 */
export function scrubLiveOnlyKeys(overrides: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(overrides ?? {}).filter(([key]) => !(LIVE_ONLY_KEYS as readonly string[]).includes(key)),
  );
}

/**
 * Whether a draft_overrides blob has any real staged content — ignores
 * response_schema/sample_output so a leftover stale key (from before
 * this fix) doesn't make the UI think there's a pending draft to
 * review or discard when there's really nothing left to act on.
 */
export function hasRealDraft(overrides: Record<string, unknown> | null | undefined): boolean {
  return Object.keys(scrubLiveOnlyKeys(overrides)).length > 0;
}

/**
 * Route each dirty field to the draft_overrides patch (role,
 * instructions, templated/technical instructions, inputs) or the
 * live-row update (response_schema, sample_output) — the two fields
 * write straight to the Prompt row and are never staged, matching
 * every other writer of those columns in this codebase.
 */
export function splitDirtyFields(
  dirty: DirtyFlags,
  values: FormValues,
  currentOverrides: Record<string, unknown> | null | undefined,
): SplitDirtyFieldsResult {
  const nextOverrides: DraftOverrides = scrubLiveOnlyKeys(currentOverrides);
  if (dirty.instructions) nextOverrides.instructions = values.instructions;
  if (dirty.role) nextOverrides.role = values.role || null;
  if (dirty.templated_instructions) {
    nextOverrides.templated_instructions = values.templated_instructions || null;
  }
  if (dirty.technical_instructions) {
    nextOverrides.technical_instructions = values.technical_instructions || null;
  }
  if (dirty.input_keys) nextOverrides.inputs = values.input_keys.map((key) => ({ key }));

  const liveUpdate: LiveUpdatePayload = {};
  if (dirty.response_schema) {
    if (!values.response_schema.trim()) {
      liveUpdate.response_schema = null;
    } else {
      try {
        liveUpdate.response_schema = JSON.parse(values.response_schema);
      } catch {
        return { nextOverrides, liveUpdate: {}, draftDirty: false, error: "Response schema is not valid JSON." };
      }
    }
  }
  if (dirty.sample_output) {
    liveUpdate.sample_output = values.sample_output || null;
  }

  const draftDirty = Boolean(
    dirty.instructions ||
    dirty.role ||
    dirty.templated_instructions ||
    dirty.technical_instructions ||
    dirty.input_keys,
  );

  return { nextOverrides, liveUpdate, draftDirty };
}

/**
 * Baseline to reset the form to when the draft PUT succeeds but the
 * subsequent live response_schema/sample_output update fails. Draft-backed
 * fields get the just-submitted values (they're saved server-side, so the
 * next reset should mark them clean); live fields keep their pre-submit
 * baseline so a `reset(..., { keepValues: true })` call leaves the user's
 * edit visible on screen and still flagged dirty there, instead of the
 * whole form looking saved when the live update actually failed (PROC-190).
 */
export function partialFailureResetBaseline(
  values: FormValues,
  liveUpdate: LiveUpdatePayload,
  priorDefaults: FormValues,
): FormValues {
  const merged = { ...values };
  for (const key of Object.keys(liveUpdate) as (keyof LiveUpdatePayload)[]) {
    merged[key] = priorDefaults[key];
  }
  return merged;
}
